import {
  Anchor,
  Box,
  Button,
  Card,
  Checkbox,
  Collapse,
  Group,
  LoadingOverlay,
  Modal,
  MultiSelect,
  SimpleGrid,
  Stack,
  Switch,
  TextInput,
  Textarea,
  Title
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { IconChevronDown, IconChevronUp, IconSparkles } from "@tabler/icons-react";
import { PropsWithChildren, useCallback, useEffect, useMemo, useState } from "react";
import ColaboradorMultiSelector from "@/modules/colaboradores/components/colaborador-multi-selector-ideias";
import ColaboradorSelector from "@/modules/colaboradores/components/colaborador-selector";
import {
  getDefaultConfiguracaoFormularioIdeias,
  TConfiguracaoFormularioIdeias
} from "@/modules/configuracao-formulario-ideias";
import useGetActiveRegulamentosIdeias from "@/modules/regulamento-ideias/hooks/useGetActiveRegulamentosIdeias";
import useUserStore from "@/modules/users/store";
import { extractBaseInputPropertiesFromFormSchema, extractTextInputPropertiesFromFormSchema } from "@/utils/form";
import { useIdeiaFormContext } from "./ideia-form-context";
import IdeiaAnexos from "./IdeiaAnexos";
import { SimilarIdea } from "../../hooks/useCheckSimilarIdeas";
import ImproveIdeaDescriptionModal from "../../modals/improve-idea-description-modal/ImproveIdeaDescriptionModal";
import SimilarIdeasModal from "../../modals/similar-ideas-modal";
import ideiaSchema from "../../schema/ideia-schema";
import CheckSimilarLink from "../check-similar-link";

const EMPTY_ARRAY: string[] = [];
const INLINE_FLEX = "inline-flex";

type IdeiaFormFieldsProperties = PropsWithChildren<{
  isLoading: boolean;
  readOnly?: boolean;
  configuracao?: TConfiguracaoFormularioIdeias;
}>;

/**
 * Sync regulamento reference with form values
 * @returns - Regulamento
 */
function useSyncRegulamentoReference() {
  return useGetActiveRegulamentosIdeias<TRegulamentoIdeias | undefined>({
    select(dataSnapshot) {
      if (dataSnapshot.length === 0) {
        return undefined;
      }

      const [firstRegulamento] = dataSnapshot;
      if (!firstRegulamento) {
        throw new Error("Regulamento não encontrado");
      }
      return firstRegulamento.data();
    }
  });
}

// eslint-disable-next-line complexity, sonarjs/cognitive-complexity
const IdeiaFormFields = ({ isLoading, children, readOnly = false, configuracao }: IdeiaFormFieldsProperties) => {
  const [opened, { toggle }] = useDisclosure(!readOnly);
  const activeEmpresaId = useUserStore((store) => store.activeEmpresaId);
  const activeEmpresa = useUserStore((state) => state.activeEmpresa);
  const { values, setFieldValue, getInputProps } = useIdeiaFormContext();

  const configToUse = useMemo(() => configuracao || getDefaultConfiguracaoFormularioIdeias(), [configuracao]);

  const fieldsDescriptor = ideiaSchema.describe();

  const { data: regulamento, isLoading: isLoadingRegulamento } = useSyncRegulamentoReference();
  // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
  const formatValue = getInputProps("autorReference").value?.split("/")[3] as string;
  const newValue = `empresas/${activeEmpresaId}/colaboradores/${formatValue}`;
  const autorReferenceValue = useMemo(() => {
    return [newValue];
  }, [newValue]);
  const descricaoProblema = getInputProps("descricaoProblema").value as string[];
  const tituloValue = typeof values.titulo === "string" ? values.titulo : "";
  const descricaoValue = typeof values.descricao === "string" ? values.descricao : "";
  const hasDescricao = Boolean(descricaoValue.trim());
  const comoAtingirObjetivoValue = typeof values.comoAtingirObjetivo === "string" ? values.comoAtingirObjetivo : "";
  const descricaoProblemaValue = typeof values.descricaoProblema === "string" ? values.descricaoProblema : "";
  const diferenciaisValue = typeof values.diferenciais === "string" ? values.diferenciais : "";
  const hasComoAtingirObjetivo = Boolean(comoAtingirObjetivoValue.trim());
  const hasDescricaoProblema = Boolean(descricaoProblemaValue.trim());
  const hasDiferenciais = Boolean(diferenciaisValue.trim());
  const setoresOptions = useMemo(
    () => activeEmpresa?.setores?.filter((setor) => typeof setor === "string") || EMPTY_ARRAY,
    [activeEmpresa?.setores]
  );

  // Estado local para controlar o modal de melhoria (evita problemas com context modals)
  const [improveModalOpened, setImproveModalOpened] = useState(false);
  const [modalInputs, setModalInputs] = useState({ titulo: "", descricao: "" });
  const [improveTargetField, setImproveTargetField] = useState<
    "descricao" | "comoAtingirObjetivo" | "descricaoProblema" | "diferenciais"
  >("descricao");

  // Estado local para controlar o modal de ideias similares em modo preview
  const [previewSimilarIdeasModalOpened, setPreviewSimilarIdeasModalOpened] = useState(false);
  const [previewSimilarIdeas, setPreviewSimilarIdeas] = useState<SimilarIdea[]>([]);

  const handleOpenImproveText = useCallback(
    (field: "descricao" | "comoAtingirObjetivo" | "descricaoProblema" | "diferenciais", text: string) => {
      if (!text.trim()) {
        return;
      }

      setImproveTargetField(field);
      setModalInputs({ titulo: tituloValue, descricao: text });
      setImproveModalOpened(true);
    },
    [tituloValue]
  );

  const handleOpenImproveDescricao = useCallback(() => {
    handleOpenImproveText("descricao", descricaoValue);
  }, [handleOpenImproveText, descricaoValue]);

  const handleOpenImproveComoAtingir = useCallback(() => {
    handleOpenImproveText("comoAtingirObjetivo", comoAtingirObjetivoValue);
  }, [handleOpenImproveText, comoAtingirObjetivoValue]);

  const handleOpenImproveDescricaoProblema = useCallback(() => {
    handleOpenImproveText("descricaoProblema", descricaoProblemaValue);
  }, [handleOpenImproveText, descricaoProblemaValue]);

  const handleOpenImproveDiferenciais = useCallback(() => {
    handleOpenImproveText("diferenciais", diferenciaisValue);
  }, [handleOpenImproveText, diferenciaisValue]);

  const handleApplySuggestion = useCallback(
    (suggestion: string) => {
      setFieldValue(improveTargetField, suggestion);
      setImproveModalOpened(false);
    },
    [improveTargetField, setFieldValue]
  );

  const handleCloseModal = useCallback(() => {
    setImproveModalOpened(false);
  }, []);

  const handlePreviewSimilarIdeasFound = useCallback((similarIdeas: SimilarIdea[]) => {
    setPreviewSimilarIdeas(similarIdeas);
    setPreviewSimilarIdeasModalOpened(true);
  }, []);

  const handleClosePreviewSimilarIdeas = useCallback(() => {
    setPreviewSimilarIdeasModalOpened(false);
  }, []);

  useEffect(() => {
    const regulamentoReferencePath =
      typeof values.regulamentoReference === "string" ? values.regulamentoReference : values.regulamentoReference?.path;
    const hasRegulamentoDifferent = regulamentoReferencePath !== regulamento?.refPath;
    if (!isLoading && regulamento && hasRegulamentoDifferent) {
      setFieldValue("regulamentoReference", regulamento.refPath);
    }
  }, [isLoading, regulamento, setFieldValue, values.regulamentoReference]);

  // prettier-ignore
  return (
    <>
      <Stack gap="md" mb="lg">
        {readOnly && descricaoProblema.length > 0 && (
          <Stack gap="xs">
            <Group justify="space-between" mb={5}>
              <Title order={4}>Informações do cadastro de ideias</Title>
              <Button
                type="button"
                variant="transparent"
                size="compact-sm"
                rightSection={opened ? <IconChevronUp size="1rem" /> : <IconChevronDown size="1rem" />}
                onClick={toggle}
              >
                Ver mais
              </Button>
            </Group>
            {children}
          </Stack>
        )}
        {(!readOnly && regulamento?.permitirIdeiaPrivada) && (
          <Checkbox
            checked={getInputProps("privado").value as boolean}
            {...getInputProps("privado")}
            {...extractBaseInputPropertiesFromFormSchema(fieldsDescriptor, "privado")}
          />
        )}
        <Group>
          {configToUse.setores.visivel && (
            <MultiSelect
              data={setoresOptions}
              style={{ flex: 1 }}
              readOnly={readOnly || !!values.id}
              variant={readOnly ? "unstyled" : "default"}
              {...getInputProps("setores")}
              {...extractTextInputPropertiesFromFormSchema(fieldsDescriptor, "setores")}
              label={configToUse.setores.titulo || "Setores"}
            />
          )}
          {readOnly && (
            <ColaboradorSelector
              searchable
              style={{ flex: 1 }}
              clearable={false}
              readOnly={readOnly}
              variant={readOnly ? "unstyled" : "default"}
              nothingFoundMessage="Nenhum colaborador encontrado..."
              {...getInputProps("autorReference")}
              {...extractTextInputPropertiesFromFormSchema(fieldsDescriptor, "autorReference")}
              value={Array.isArray(autorReferenceValue) ? autorReferenceValue[0] : autorReferenceValue}
            />
          )}
        </Group>

        <TextInput
          type="text"
          style={{ flex: 1 }}
          readOnly={readOnly}
          variant={readOnly ? "unstyled" : "default"}
          {...getInputProps("titulo")}
          {...extractTextInputPropertiesFromFormSchema(fieldsDescriptor, "titulo")}
        />
        <Box>
          {!readOnly && (
            <Box
              component="label"
              style={{ fontSize: '0.875rem', fontWeight: 500, display: INLINE_FLEX, alignItems: 'center', gap: '2px' }}
              mb="xs"
            >
              Descrição
              <Box component="span" style={{ color: 'var(--mantine-color-error)' }}>
                *
              </Box>
            </Box>
          )}
          <Textarea
            style={{ flex: 1 }}
            readOnly={readOnly}
            variant={readOnly ? "unstyled" : "default"}
            className="textarea-lg"
            autosize
            minRows={2}
            maxRows={5}
            {...getInputProps("descricao")}
            {...(() => {
              const { label, ...rest } = extractTextInputPropertiesFromFormSchema(fieldsDescriptor, "descricao");
              return readOnly ? { label, ...rest } : rest;
            })()}
          />
          {!readOnly && (
            <Group justify="flex-start" mt="xs">
              <CheckSimilarLink
                titulo={values.titulo}
                descricao={values.descricao}
                disabled={readOnly}
                onSimilarIdeasFound={handlePreviewSimilarIdeasFound}
              />
              {hasDescricao && (
                <Anchor
                  size="sm"
                  component="button"
                  type="button"
                  style={{ display: INLINE_FLEX, alignItems: "center", gap: "4px" }}
                  onClick={handleOpenImproveDescricao}
                  aria-label="Melhorar descrição com IA"
                >
                  <IconSparkles size={14} aria-hidden="true" />
                  Melhorar com IA
                </Anchor>
              )}
            </Group>
          )}
        </Box>
        <Collapse in={opened}>
          <Stack gap="md">
            {configToUse.objetivo.visivel && (
              <Checkbox.Group
                defaultValue={EMPTY_ARRAY}
                {...getInputProps("objetivo")}
                {...extractBaseInputPropertiesFromFormSchema(fieldsDescriptor, "objetivo")}
                label={configToUse.objetivo.titulo || "A sua ideia tem como objetivo:"}
              >
                <SimpleGrid cols={2} spacing="sm" verticalSpacing="sm" mt="md">
                  {readOnly
                    ? values.objetivo.map((objetivo) => (
                      <Checkbox key={objetivo} value={objetivo} variant="unstyled" checked={true} label={objetivo} />
                    ))
                    : (configToUse.objetivos || []).map((objetivo) => (
                      <Checkbox key={objetivo} value={objetivo} label={objetivo} />
                    ))}
                </SimpleGrid>
              </Checkbox.Group>
            )}
            {configToUse.comoAtingirObjetivo?.visivel && (
              <Box>
                <Textarea
                  readOnly={readOnly}
                  variant={readOnly ? "unstyled" : "default"}
                  className="textarea-lg"
                  autosize
                  minRows={2}
                  maxRows={8}
                  {...getInputProps("comoAtingirObjetivo")}
                  {...extractTextInputPropertiesFromFormSchema(fieldsDescriptor, "comoAtingirObjetivo")}
                  label={configToUse.comoAtingirObjetivo?.titulo || "Descreva como a ideia pode contribuir para que algum dos objetivos acima seja atingido."}
                />
                {!readOnly && hasComoAtingirObjetivo && (
                  <Group justify="flex-start" mt="xs">
                    <Anchor
                      size="sm"
                      component="button"
                      type="button"
                      style={{ display: INLINE_FLEX, alignItems: "center", gap: "4px" }}
                      onClick={handleOpenImproveComoAtingir}
                      aria-label="Melhorar texto com IA"
                    >
                      <IconSparkles size={14} aria-hidden="true" />
                      Melhorar com IA
                    </Anchor>
                  </Group>
                )}
              </Box>
            )}
            {configToUse.descricaoProblema?.visivel && (
              <Box>
                <Textarea
                  readOnly={readOnly}
                  variant={readOnly ? "unstyled" : "default"}
                  className="textarea-lg"
                  autosize
                  minRows={2}
                  maxRows={8}
                  {...getInputProps("descricaoProblema")}
                  {...extractTextInputPropertiesFromFormSchema(fieldsDescriptor, "descricaoProblema")}
                  label={configToUse.descricaoProblema?.titulo || "Descreva aqui o problema que sua ideia irá ajudar a solucionar. Apresente detalhes do problema e o impacto disso."}
                />
                {!readOnly && hasDescricaoProblema && (
                  <Group justify="flex-start" mt="xs">
                    <Anchor
                      size="sm"
                      component="button"
                      type="button"
                      style={{ display: INLINE_FLEX, alignItems: "center", gap: "4px" }}
                      onClick={handleOpenImproveDescricaoProblema}
                      aria-label="Melhorar texto com IA"
                    >
                      <IconSparkles size={14} aria-hidden="true" />
                      Melhorar com IA
                    </Anchor>
                  </Group>
                )}
              </Box>
            )}
            {configToUse.diferenciais?.visivel && (
              <Box>
                <Textarea
                  readOnly={readOnly}
                  variant={readOnly ? "unstyled" : "default"}
                  className="textarea-sm"
                  autosize
                  minRows={2}
                  maxRows={8}
                  {...getInputProps("diferenciais")}
                  {...extractTextInputPropertiesFromFormSchema(fieldsDescriptor, "diferenciais")}
                  label={configToUse.diferenciais?.titulo || "Que diferenciais inovadores e tecnológicos seu Projeto/Ideia possui?"}
                />
                {!readOnly && hasDiferenciais && (
                  <Group justify="flex-start" mt="xs">
                    <Anchor
                      size="sm"
                      component="button"
                      type="button"
                      style={{ display: INLINE_FLEX, alignItems: "center", gap: "4px" }}
                      onClick={handleOpenImproveDiferenciais}
                      aria-label="Melhorar texto com IA"
                    >
                      <IconSparkles size={14} aria-hidden="true" />
                      Melhorar com IA
                    </Anchor>
                  </Group>
                )}
              </Box>
            )}
            {configToUse.feedback?.visivel && (
              <Textarea
                readOnly={readOnly}
                variant={readOnly ? "unstyled" : "default"}
                className="textarea-sm"
                autosize
                minRows={2}
                maxRows={8}
                {...getInputProps("feedback")}
                {...extractTextInputPropertiesFromFormSchema(fieldsDescriptor, "feedback")}
                label={configToUse.feedback?.titulo || "Você já buscou o feedback para validação do seu Projeto/Ideia? Se sim, qual foi o resultado?"}
              />
            )}
            {configToUse.participantesReferences?.visivel && (
              <ColaboradorMultiSelector
                style={{ flex: 1 }}
                searchable
                multiple
                readOnly={readOnly}
                variant={readOnly ? "unstyled" : "default"}
                nothingFoundMessage="Nenhum participante encontrado..."
                {...getInputProps("participantesReferences")}
                {...extractTextInputPropertiesFromFormSchema(fieldsDescriptor, "participantesReferences")}
                label={configToUse.participantesReferences?.titulo || "Participantes da ideia"}
                description={"Preencha este campo caso haja mais participantes além de você."}
              />
            )}
            {configToUse.anexos?.visivel && (
              <IdeiaAnexos isLoading={isLoading} readOnly={readOnly} />
            )}
            <Box pos="relative">
              {isLoadingRegulamento && <LoadingOverlay visible={true} />}
              {regulamento?.permitirControlarInteracao && (
                <Card withBorder mt="lg">
                  <Group justify="space-between">
                    <Switch
                      disabled={readOnly}
                      defaultChecked={values.permitirComentarios}
                      variant={readOnly ? "unstyled" : "default"}
                      onLabel="Sim"
                      offLabel="Não"
                      size="md"
                      {...getInputProps("permitirComentarios")}
                      {...extractBaseInputPropertiesFromFormSchema(fieldsDescriptor, "permitirComentarios")}
                    />
                    <Switch
                      disabled={readOnly}
                      variant={readOnly ? "unstyled" : "default"}
                      defaultChecked={values.permitirPatrocinio}
                      onLabel="Sim"
                      offLabel="Não"
                      size="md"
                      {...getInputProps("permitirPatrocinio")}
                      {...extractBaseInputPropertiesFromFormSchema(fieldsDescriptor, "permitirPatrocinio")}
                    />
                  </Group>
                </Card>
              )}
            </Box>
          </Stack>
        </Collapse>
      </Stack>

      {/* Modal local para melhorar descrição (evita problemas com context modals) */}
      <Modal
        opened={improveModalOpened}
        onClose={handleCloseModal}
        title="Melhorar com IA"
        size="lg"
        withinPortal={true}
        zIndex={1000}
        overlayProps={{ blur: 3 }}
      >
        <ImproveIdeaDescriptionModal
          titulo={modalInputs.titulo}
          descricao={modalInputs.descricao}
          onApply={handleApplySuggestion}
          onCancel={handleCloseModal}
        />
      </Modal>

      {/* Modal local para preview de ideias similares (evita problemas com context modals) */}
      <Modal
        opened={previewSimilarIdeasModalOpened}
        onClose={handleClosePreviewSimilarIdeas}
        title="Ideias Similares Encontradas"
        size="lg"
        withinPortal={true}
        zIndex={1000}
        overlayProps={{ blur: 3 }}
      >
        <SimilarIdeasModal
          similarIdeas={previewSimilarIdeas}
          onConfirm={handleClosePreviewSimilarIdeas}
          onCancel={handleClosePreviewSimilarIdeas}
          previewMode={true}
        />
      </Modal>
    </>
  );
};

export default IdeiaFormFields;
